import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiCopy, FiLink, FiCheck, FiTrash2 } from 'react-icons/fi';
import { DataRoomShareLink, getShareUrl } from '../../types/dataRoom';

// Type cast icons for React 18 compatibility
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const CopyIcon = FiCopy as React.FC<React.SVGProps<SVGSVGElement>>;
const LinkIcon = FiLink as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = FiCheck as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;

interface ShareModalProps {
  dataRoomId: string;
  dataRoomName: string;
  shareLinks: DataRoomShareLink[];
  onClose: () => void;
  onCreateLink: () => Promise<DataRoomShareLink>;
  onDeleteLink: (linkId: string) => Promise<void>;
  onToggleLink: (linkId: string, isActive: boolean) => Promise<void>;
}

const ShareModal: React.FC<ShareModalProps> = ({
  dataRoomId,
  dataRoomName,
  shareLinks,
  onClose,
  onCreateLink,
  onDeleteLink,
  onToggleLink,
}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isCreating) {
      onClose();
    }
  };

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isCreating) {
        onClose();
      }
    },
    [onClose, isCreating]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleCreateLink = async () => {
    setIsCreating(true);
    setError(null);
    try {
      await onCreateLink();
    } catch (err: any) {
      setError(err?.message || 'Failed to create share link');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = async (urlSlug: string | null, shareToken: string, linkId: string) => {
    const url = getShareUrl(urlSlug || shareToken, dataRoomName);
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(linkId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      setError('Failed to copy link to clipboard');
    }
  };

  const handleToggle = async (link: DataRoomShareLink) => {
    try {
      await onToggleLink(link.id, !link.is_active);
    } catch (err: any) {
      setError(err?.message || 'Failed to update link');
    }
  };

  const handleDelete = async (linkId: string) => {
    try {
      await onDeleteLink(linkId);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete link');
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const modal = (
    <div className="su-dataroom-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-dataroom-modal" style={{ maxWidth: '560px' }} role="dialog" aria-modal="true" aria-labelledby="share-modal-title">
        <div className="su-dataroom-modal-header">
          <div>
            <h2 id="share-modal-title" className="su-dataroom-modal-title">Share Data Room</h2>
            <p className="su-dataroom-modal-subtitle">
              Create and manage share links for {dataRoomName}
            </p>
          </div>
          <button className="su-dataroom-modal-close" onClick={onClose} aria-label="Close modal">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <div className="su-dataroom-modal-body">
          {error && (
            <div
              style={{
                color: '#f87171',
                fontSize: '14px',
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.2)',
              }}
            >
              {error}
            </div>
          )}

          {/* Create new link button */}
          <button
            className="su-btn-primary"
            onClick={handleCreateLink}
            disabled={isCreating}
            style={{ width: '100%', marginBottom: '20px' }}
          >
            <LinkIcon width={16} height={16} />
            <span>{isCreating ? 'Creating...' : 'Create New Share Link'}</span>
          </button>

          {/* Share links list */}
          {shareLinks.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '32px 16px',
                color: '#666',
              }}
            >
              <LinkIcon style={{ width: 32, height: 32, marginBottom: 12, opacity: 0.5 }} />
              <p style={{ margin: 0 }}>No share links yet</p>
              <p style={{ margin: '8px 0 0', fontSize: '13px', color: '#888' }}>
                Create a link to share this data room
              </p>
            </div>
          ) : (
            <div className="su-share-links-list">
              {shareLinks.map((link) => (
                <div
                  key={link.id}
                  className={`su-share-link-item ${!link.is_active ? 'inactive' : ''}`}
                >
                  <div className="su-share-link-info">
                    <div className="su-share-link-url">
                      {getShareUrl(link.url_slug || link.share_token, dataRoomName)}
                    </div>
                    <div className="su-share-link-meta">
                      <span
                        className={link.is_active ? 'su-badge-active' : 'su-badge-draft'}
                      >
                        {link.is_active ? 'Active' : 'Inactive'}
                      </span>
                      <span style={{ color: '#666', fontSize: '12px' }}>
                        Created {formatDate(link.created_at)}
                      </span>
                      {link.expires_at && (
                        <span style={{ color: '#666', fontSize: '12px' }}>
                          Expires {formatDate(link.expires_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="su-share-link-actions">
                    <button
                      className="su-share-link-btn"
                      onClick={() => handleCopyLink(link.url_slug ?? null, link.share_token, link.id)}
                      title="Copy link"
                      aria-label="Copy link"
                    >
                      {copiedId === link.id ? (
                        <CheckIcon width={16} height={16} style={{ color: '#22c55e' }} />
                      ) : (
                        <CopyIcon width={16} height={16} />
                      )}
                    </button>
                    <button
                      className="su-share-link-btn"
                      onClick={() => handleToggle(link)}
                      title={link.is_active ? 'Deactivate' : 'Activate'}
                      aria-label={link.is_active ? 'Deactivate link' : 'Activate link'}
                    >
                      <span style={{ fontSize: '12px' }}>
                        {link.is_active ? 'Disable' : 'Enable'}
                      </span>
                    </button>
                    <button
                      className="su-share-link-btn delete"
                      onClick={() => handleDelete(link.id)}
                      title="Delete link"
                      aria-label="Delete link"
                    >
                      <TrashIcon width={16} height={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="su-dataroom-modal-footer">
          <button className="su-btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default ShareModal;
